import { createContext } from 'react';
import type { AuthUser } from '../api';

/**
 * RBAC R.2b (#314) web-side auth state — the real session identity that fills the
 * R.1 (#313) scaffold. A single context every component reads "who is signed in /
 * does the server enforce auth" from, instead of poking localStorage. The
 * <AuthProvider> sources `user` from a verified session (GET /api/auth/me) and
 * `authEnforced` from the server's GET /api/auth/config — the SINGLE source of
 * truth for whether the root gate blocks. With AUTH_ENFORCED shipped OFF,
 * `authEnforced` is false and the COP renders exactly as today (non-breaking);
 * R.2c flips the server flag and the gate enforces automatically.
 */
export type AuthStatus = 'loading' | 'ready';

export interface AuthState {
  /** The authenticated operator, or null when logged out / not yet resolved. */
  readonly user: AuthUser | null;
  /** Server-driven (GET /api/auth/config): does the API enforce auth? When true and
   *  `user` is null, the root gate shows the login screen. Ships false (R.2b). */
  readonly authEnforced: boolean;
  /** 'loading' until the boot config + /me probe resolves, then 'ready'. The gate
   *  shows a splash (not a COP flash) while loading. */
  readonly status: AuthStatus;
  /** Attempt a login. Resolves true on success (`user` is then populated), false on
   *  a generic failure (no enumeration leak). */
  login(email: string, password: string): Promise<boolean>;
  /** Revoke the session + clear identity. */
  logout(): Promise<void>;
  /** Re-probe GET /api/auth/me (e.g. after a mid-session 401 clears identity). */
  refresh(): Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);
